import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import { useUserStore } from '../../modules/Auth/stores/authStore';
import { useTourStore } from './tourStore';
import { TOUR_DEFINITIONS } from './config';
import type { TourDefinition } from './types';

function matchRoute(pattern: string, pathname: string): boolean {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');

  if (patternParts.length !== pathParts.length) return false;

  return patternParts.every(
    (part, i) => part.startsWith(':') || part === pathParts[i]
  );
}

function isElementVisible(selector: string): boolean {
  const el = document.querySelector(selector);
  return !!el && el.getBoundingClientRect().width > 0;
}

function getVisibleSteps(steps: TourDefinition['steps']): TourDefinition['steps'] {
  // Se o primeiro step está visível, mantém todos os demais
  const firstSelector = typeof steps[0]?.element === 'string' ? steps[0].element : null;
  if (firstSelector && isElementVisible(firstSelector)) {
    return [...steps];
  }

  // Caso contrário, filtra apenas os visíveis
  return steps.filter((step) => {
    const selector = typeof step.element === 'string' ? step.element : null;
    if (!selector) return true;
    return isElementVisible(selector);
  });
}

interface UseTourOptions {
  route?: string;
  flow?: string;
  delay?: number;
  autoStart?: boolean;
}

export function useTour(options: UseTourOptions = {}) {
  const { autoStart = true, delay = 500, flow, route } = options;
  const { user } = useUserStore();
  const { hasSeen, markSeen } = useTourStore();
  const { pathname } = useLocation();
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  const currentRoute = route ?? pathname;

  const pendingTours = useMemo(() => {
    if (!user) return [];

    return TOUR_DEFINITIONS
      .filter((t) => matchRoute(t.route, currentRoute))
      .filter((t) => !flow || t.flow === flow)
      .filter((t) => !t.condition || t.condition(user))
      .filter((t) => !t.dependsOn || hasSeen(user.id, t.dependsOn))
      .sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99))
      .filter((t) => !hasSeen(user.id, t.id));
  }, [currentRoute, flow, user, hasSeen]);

  const pendingTour = pendingTours[0];

  const start = useCallback(
    (tourId?: string) => {
      if (!user) return;

      const toursToTry = tourId
        ? [TOUR_DEFINITIONS.find((t) => t.id === tourId)].filter(Boolean) as TourDefinition[]
        : pendingTours;

      if (toursToTry.length === 0) return;

      let tour: TourDefinition | undefined;
      let visibleSteps: TourDefinition['steps'] = [];

      for (const candidate of toursToTry) {
        const steps = getVisibleSteps(candidate.steps);
        if (steps.length > 0) {
          tour = candidate;
          visibleSteps = steps;
          break;
        }
      }

      if (!tour || visibleSteps.length === 0) return;

      const activeTour = tour;
      const d = driver({
        showProgress: true,
        animate: true,
        nextBtnText: 'Próximo',
        prevBtnText: 'Anterior',
        doneBtnText: 'Concluir',
        progressText: '{{current}} de {{total}}',
        ...tour.driverOptions,
        steps: visibleSteps,
        onDestroyStarted: () => {
          markSeen(user.id, activeTour.id);
          d.destroy();
        },
      });

      driverRef.current = d;
      d.drive();
    },
    [user, pendingTour, markSeen]
  );

  useEffect(() => {
    if (!autoStart || !pendingTour || !user) return;

    const timeout = setTimeout(() => start(), delay);

    return () => {
      clearTimeout(timeout);
      driverRef.current?.destroy();
    };
  }, [autoStart, pendingTour?.id, user?.id, delay]);

  return {
    start,
    pendingTour,
    hasPendingTour: !!pendingTour,
  };
}
